import { useState, type ReactElement } from 'react';
import {
  Form,
  Link,
  redirect,
  useActionData,
  useFetcher,
  useLoaderData,
  useNavigation,
  useSubmit,
} from 'react-router';
import type { ActionFunctionArgs, LoaderFunctionArgs } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Alert, Badge, Button, Dialog, EmptyState, Rating } from '@marketplace/ui';
import {
  HANDSHAKE_SHORT_CODE_PATTERN,
  type Auction,
  type AuctionBid,
  type BookingPayment,
  type Job,
  type JobAsk,
  type JobThreadList,
  type JobThreadView,
  type Quote,
} from '@marketplace/contracts';
import { localeOf } from '../features/auth/actions.js';
import { AWARD_WINDOW_DAYS } from '../features/auctions/auction-form.js';
import { AuctionHeader, durationDaysOf } from '../features/auctions/AuctionHeader.js';
import { Countdown } from '../features/auctions/Countdown.js';
import { JobFacts } from '../features/jobs/JobFacts.js';
import { handshakeErrorKey } from '../features/jobs/handshake.js';
import { StartPanel, type RedeemActionData } from '../features/jobs/StartPanel.js';
import { ApiError } from '../shared/api.js';
import { CallOutCheckout } from '../features/checkout/CallOutCheckout.js';
import {
  AskDraftPublish,
  AskPaidFor,
  AskReturnNotice,
  AskUnavailable,
} from '../features/checkout/ask/Ask.js';
import {
  askReturnOf,
  charging,
  loadAskPrices,
  loadJobAsk,
  runAskAction,
  type AskPricesRead,
  type AskReturn,
} from '../features/checkout/ask/ask.js';
import { bookingErrorKey } from '../features/checkout/payment-errors.js';
import { AcceptedQuote } from '../features/quotes/AcceptedQuote.js';
import { unacceptErrorKey } from '../features/quotes/unaccept.js';
import { QUOTE_SORTS, cheapestOf, rankQuotes, type QuoteSort } from '../features/quotes/ranking.js';
import { QuotePrice } from '../features/quotes/QuotePrice.js';
import { Thread } from '../features/messages/Thread.js';
import { sendMessage, type MessageActionData } from '../features/messages/message-form.js';
import { usePoll } from '../features/messages/poll.js';
import { loadJobThread, loadJobThreads } from '../features/messages/thread-query.js';
import { queryKeys, routeContext } from '../shared/query.js';
import { loadSession } from '../shared/session.js';
import { jobTone, quoteTone } from '../shared/status-tone.js';

/**
 * One job, and the quotes to choose between — `W4-T04` §3.2, §3.3.
 *
 * **The loader assembles the whole set rather than one page**, and that is the design rather than
 * laziness about paging: the ranking is *rating, then price* over every offer on the job, and a
 * ranking applied to the first twenty of forty quotes is not a worse comparison, it is a wrong one.
 * So it follows the cursor until the API says there is no more, bounded by `MAX_QUOTES`.
 *
 * Which is also the honest use of the cursor `W4-T04` added. The API needed one because the list is
 * written by **other people** in numbers the owner does not control; the screen needs the whole set
 * because it sorts. Those are the same requirement seen from two ends.
 */

/** The bound. Five pages of the contract's default, and a job that reaches it is pathological. */
const MAX_QUOTES = 100;

interface JobData {
  locale: string;
  job: Job;
  quotes: Quote[];
  /** True when the job has more quotes than this screen will load. Rendered, never hidden. */
  truncated: boolean;
  /** `W4-T06` — one conversation per provider who wrote. `null` when it could not be loaded. */
  threads: JobThreadList | null;
  /** The conversation `?thread=` names, when it is one of this job's. */
  selected: JobThreadView | null;
  /** `W6-T06` — the auction, for a job published as one; `null` for presupuestos and drafts. */
  auction: Auction | null;
  /** The live bids, cheapest first — the client sees them as they arrive. */
  bids: AuctionBid[];
  /** The loader's clock, so the countdown renders the same wherever it renders. */
  now: string;
  /**
   * `W5-T12` — what asking costs (read for a draft, and when another window may be bought), what the
   * owner paid for on a published job, and where Checkout came back to.
   */
  askPrices: AskPricesRead;
  jobAsk: JobAsk | null;
  askReturn: AskReturn | null;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export async function loader({ params, request, context }: LoaderFunctionArgs): Promise<JobData> {
  const locale = localeOf(params);

  const session = await loadSession(context);
  if (session === null) throw redirect(`/${locale}/login`);

  const id = params['id'] ?? '';
  const { queryClient, api } = context.get(routeContext);

  try {
    const job = await queryClient.ensureQueryData({
      queryKey: queryKeys.job(id),
      queryFn: () => api.getJob(id),
    });
    const isAuction = job.mode === 'AUCTION' && job.status !== 'DRAFT';

    // An auction job has no quotes — the API refuses them — so it does not ask for any.
    const quotes = isAuction
      ? []
      : await queryClient.ensureQueryData({
          queryKey: queryKeys.jobQuotes(id),
          queryFn: async () => {
            const collected: Quote[] = [];
            let cursor: string | undefined;

            // Bounded by `MAX_QUOTES` *and* by the page count, so a cursor the API kept handing back
            // unchanged would end this loop rather than spin it.
            while (collected.length < MAX_QUOTES) {
              const page = await api.getJobQuotes(id, cursor);
              collected.push(...page.items);
              if (!page.page.hasMore || page.page.nextCursor === null) break;
              cursor = page.page.nextCursor;
            }
            return collected;
          },
        });

    // `W6-T06`. Fetched fresh (`staleTime: 0`), not served from the cache: the close happens on the
    // server while this page is open, and the poll is how the screen finds out.
    const auction = isAuction
      ? await queryClient.fetchQuery({
          queryKey: queryKeys.jobAuction(id),
          queryFn: () => api.getJobAuction(id),
          staleTime: 0,
        })
      : null;
    // The client sees bids from the first one (operator, 2026-10-01) — fetched fresh, so the poll
    // shows each as it arrives.
    const bids =
      auction !== null
        ? await queryClient.fetchQuery({
            queryKey: queryKeys.auctionBids(id),
            queryFn: () => api.getAuctionBids(id),
            staleTime: 0,
          })
        : [];

    // `?thread=` selects a conversation. Checked before asking: a malformed id is not a request
    // worth sending, and the API would answer it with the same 404 an unknown one gets.
    const threadId = new URL(request.url).searchParams.get('thread');
    const [threads, selected] = await Promise.all([
      loadJobThreads(context, id),
      threadId !== null && UUID.test(threadId)
        ? loadJobThread(context, id, threadId)
        : Promise.resolve(null),
    ]);

    const jobAsk = job.status === 'DRAFT' ? null : await loadJobAsk(context, id);
    const askPrices =
      job.status === 'DRAFT' || jobAsk?.canBuyWindow === true ? await loadAskPrices(context) : null;

    return {
      locale,
      job,
      quotes: quotes.slice(0, MAX_QUOTES),
      truncated: quotes.length > MAX_QUOTES,
      threads,
      selected,
      auction,
      bids,
      now: new Date().toISOString(),
      askPrices,
      jobAsk,
      askReturn: askReturnOf(request.url),
    };
  } catch (error) {
    // Somebody else's job, or none — one answer, because the API gives one answer (`W4-T01` §3).
    if (error instanceof ApiError && error.status === 404) {
      throw new Response('Not found', { status: 404 });
    }
    throw error;
  }
}

/**
 * `W4-T17` — what Award and pay answers, for the quote it booked: the payment to mount, or a booking
 * refusal as a key.
 */
type BookActionData = { quoteId: string } & ({ payment: BookingPayment } | { error: string });

/**
 * `W4-T17` — what Undo acceptance answers. The **code**, not a key: which sentence a `CONFLICT` is
 * depends on the job as the page reads it after the re-read (`unacceptErrorKey`). `null` is success.
 */
interface UnacceptActionData {
  quoteId: string;
  code: string | null;
}

/** The code of a refusal, or one no switch knows when the request never got an answer we parse. */
const codeOf = (error: unknown): string => (error instanceof ApiError ? error.message : 'UNKNOWN');

/**
 * The decision, posted to the route that renders it.
 *
 * A route action rather than a handler calling the client directly: a write goes through the
 * router so that the loader re-runs afterwards and the screen shows the server's answer, not the
 * one it assumed. Both caches are invalidated — the quote list because a status changed, and the
 * job because `W4-T05` will make an award change it.
 */
export async function action({
  params,
  request,
  context,
}: ActionFunctionArgs): Promise<
  | { error: string }
  | MessageActionData
  | BookActionData
  | UnacceptActionData
  | RedeemActionData
  | Response
  | null
> {
  const form = await request.formData();
  const id = params['id'] ?? '';
  const { queryClient, api } = context.get(routeContext);
  const toLogin = () => redirect(`/${localeOf(params)}/login`);
  const unauthenticated = (error: unknown) => error instanceof ApiError && error.status === 401;

  // `W4-T17` §2.2: Award and pay. Posted by a fetcher, so the answer never replaces a decision's.
  // The payment stays in memory — never the URL, storage or history (`W5-T15` AC 28) — and nothing is
  // invalidated, because booking moves nothing on the jobs side until the payment settles.
  if (form.get('intent') === 'book') {
    const quoteId = String(form.get('quoteId') ?? '');
    try {
      return { quoteId, payment: await api.createBooking(quoteId) };
    } catch (error) {
      if (unauthenticated(error)) throw toLogin();
      const refusal = error instanceof ApiError ? error : new ApiError(undefined, 'UNKNOWN');
      return { quoteId, error: bookingErrorKey(refusal) };
    }
  }

  // `W4-T17` §2.2: Undo acceptance. Success and refusal both re-read the job and its quotes, so the
  // page shows the server's answer — and so a `CONFLICT` can be read off what refused it (§6).
  if (form.get('intent') === 'unaccept') {
    const quoteId = String(form.get('quoteId') ?? '');
    let code: string | null = null;
    try {
      await api.unacceptQuote(quoteId);
    } catch (error) {
      if (unauthenticated(error)) throw toLogin();
      code = codeOf(error);
    }
    await queryClient.invalidateQueries({ queryKey: queryKeys.job(id), refetchType: 'all' });
    return { quoteId, code };
  }

  // `W4-T09` §4.4: the short code, typed on this job's page and redeemed against this job only. A
  // malformed one is refused here, so it never reaches the API and never counts as an attempt.
  if (form.get('intent') === 'redeem') {
    const shortCode = String(form.get('shortCode') ?? '').trim();
    if (!HANDSHAKE_SHORT_CODE_PATTERN.test(shortCode)) {
      return { redeemError: 'handshake.error.codeInvalid' };
    }
    try {
      await api.redeemHandshake({ jobId: id, shortCode });
    } catch (error) {
      if (unauthenticated(error)) throw toLogin();
      return { redeemError: handshakeErrorKey(codeOf(error)) };
    }
    await queryClient.invalidateQueries({ queryKey: queryKeys.job(id), refetchType: 'all' });
    await queryClient.invalidateQueries({ queryKey: queryKeys.myJobs(), refetchType: 'all' });
    return { redeemError: null };
  }

  // `W4-T06`: a reply, posted by a fetcher so it never replaces a decision's answer. The loader that
  // re-runs afterwards reads the thread fresh (`thread-query.ts`).
  if (form.get('intent') === 'message') {
    const threadId = String(form.get('threadId') ?? '');
    return sendMessage(form, (body) => api.sendJobThreadMessage(id, threadId, body));
  }

  // `W5-T12` §4.3, §4.4: pay to publish the draft, or for another window — both leave for Checkout.
  const asked = await runAskAction(context, localeOf(params), id, form);
  if (asked !== undefined) return asked;

  // `W4-T10` §2.4: the one other thing a client does here — publish a draft. A draft is what a
  // failed publish on the posting form leaves behind, and this is where that form sends them.
  if (form.get('intent') === 'publish') {
    try {
      await api.publishJob(id);
    } catch {
      return { error: 'job.publish.error' };
    }
    await queryClient.invalidateQueries({ queryKey: queryKeys.job(id), refetchType: 'all' });
    await queryClient.invalidateQueries({ queryKey: queryKeys.myJobs(), refetchType: 'all' });
    return null;
  }

  // `W6-T06`: the client chooses a bid, after close. `job(id)` is a prefix of the auction's keys.
  if (form.get('intent') === 'award') {
    try {
      await api.awardAuction(id, String(form.get('bidId') ?? ''));
    } catch (error) {
      // `W6-T09`: a 409 is final — the award window has ended, or a bid was already chosen — and
      // "try again" would send the client round in circles.
      const closed = error instanceof ApiError && error.status === 409;
      return { error: closed ? 'auction.award.closed' : 'auction.award.error' };
    }
    await queryClient.invalidateQueries({ queryKey: queryKeys.job(id), refetchType: 'all' });
    return null;
  }

  const quoteId = String(form.get('quoteId') ?? '');
  const decision = String(form.get('decision') ?? '');
  if (decision !== 'accept' && decision !== 'reject') return { error: 'job.quotes.error' };

  try {
    await api.decideQuote(quoteId, decision);
  } catch {
    // The message is a key, not a sentence: the component translates, and an action that returned
    // prose would be the one place in this app that decides what language somebody reads.
    return { error: 'job.quotes.error' };
  }

  /**
   * **`refetchType: 'all'`, and without it the decision never reached the screen** (`W4-T10`).
   *
   * Nothing *observes* these queries — the loader reads them with `ensureQueryData` and the
   * component reads the loader — so a plain invalidation only marks them stale, and
   * `ensureQueryData` hands stale data straight back. The accepted quote went on offering *Aceptar*.
   * `'all'` refetches inactive queries too, and the invalidation is awaited, so the loader that runs
   * next reads the server's answer. `job(id)` is a prefix of `jobQuotes(id)`, so one call is both.
   */
  await queryClient.invalidateQueries({ queryKey: queryKeys.job(id), refetchType: 'all' });
  return null;
}

/** The job page's wording of a provider's rating, rendered by the DS `Rating` (§3.4). */
function ProviderRating({
  provider,
  locale,
}: {
  provider: { ratingAvg: number | null; ratingCount: number };
  locale: string;
}): ReactElement {
  const { t } = useTranslation();
  const { ratingAvg, ratingCount } = provider;

  return (
    <Rating
      average={ratingAvg}
      text={
        ratingAvg === null
          ? t('job.quotes.unrated')
          : t('job.quotes.rating', {
              rating: ratingAvg.toLocaleString(locale, { maximumFractionDigits: 1 }),
              count: ratingCount,
            })
      }
    />
  );
}

/**
 * What the job asked for, and whether this provider lists it.
 *
 * `W4-T03` built coverage to be *shown* and to enforce nothing — *"Nothing enforces, but stated
 * clearly"* — and this is the screen that shows it. It makes no claim about verification, because
 * nothing in the schema knows one: `W8` is unbuilt, and a badge here would be an invention.
 */
function Coverage({ quote, locale }: { quote: Quote; locale: string }): ReactElement | null {
  const { t } = useTranslation();
  if (quote.coverage.length === 0) return null;

  return (
    <ul className="mp-meta">
      {quote.coverage.map((entry) => (
        <li key={entry.slug}>
          {locale.startsWith('en') ? entry.nameEn : entry.nameEs}
          {' — '}
          {entry.listedByProvider
            ? t('job.quotes.coverage.listed')
            : t('job.quotes.coverage.notListed')}
          {entry.requiresLicence ? ` · ${t('job.quotes.coverage.licence')}` : ''}
        </li>
      ))}
    </ul>
  );
}

export function Component(): ReactElement {
  const { t } = useTranslation();
  const {
    locale,
    job,
    quotes,
    truncated,
    threads,
    selected,
    auction,
    bids,
    now,
    askPrices,
    jobAsk,
    askReturn,
  } = useLoaderData<JobData>();
  const navigation = useNavigation();
  const submit = useSubmit();
  // `W4-T17`: the award's two writes. Fetchers, so their answers outlive the loader re-running.
  const book = useFetcher<BookActionData>();
  const unaccept = useFetcher<UnacceptActionData>();
  // An undo's refusal is shown until the client does something else on this page.
  const [settledUndo, setSettledUndo] = useState<UnacceptActionData | null>(null);
  // `W4-T18`: an award's answer stops counting once its own quote is accepted again. The quote was
  // un-accepted in between, which succeeds only with no payment pending, so that payment is dead.
  const [settledBook, setSettledBook] = useState<BookActionData | null>(null);
  usePoll();

  // Presentation state, and the only state on this page: which order the client is reading in.
  const [sort, setSort] = useState<QuoteSort>('recommended');
  // Which quote the confirmation dialog is asking about. `null` is "no dialog open".
  const [confirming, setConfirming] = useState<Quote | null>(null);

  const ranked = rankQuotes(quotes, sort);
  const cheapest = cheapestOf(ranked);
  const busy = navigation.state !== 'idle' || book.state !== 'idle' || unaccept.state !== 'idle';
  const actionData = useActionData<{ error: string } | null>();
  const publishError =
    job.status === 'DRAFT' && actionData !== undefined && actionData !== null
      ? actionData.error
      : null;

  const money = (cents: number): string =>
    (cents / 100).toLocaleString(locale, { style: 'currency', currency: 'EUR' });

  const decide = (quote: Quote, decision: 'accept' | 'reject'): void => {
    setConfirming(null);
    setSettledUndo(unaccept.data ?? null);
    if (decision === 'accept' && book.data?.quoteId === quote.id) setSettledBook(book.data);
    void submit({ quoteId: quote.id, decision }, { method: 'post' });
  };

  const awardable = (quote: Quote): boolean => quote.status === 'ACCEPTED' && job.status === 'OPEN';
  // What the last Award and pay answered, for the quote it booked and only that one, until settled.
  const booked = (quote: Quote) =>
    book.data?.quoteId === quote.id && book.data !== settledBook ? book.data : undefined;
  const checkoutFor = (quote: Quote): ReactElement | null => {
    const data = booked(quote);
    // `W5-T15` owns everything from here: Pay, 3DS, and the hand-over to the booking page.
    return data !== undefined && 'payment' in data ? (
      <CallOutCheckout payment={data.payment} locale={locale} />
    ) : null;
  };
  const bookErrorFor = (quote: Quote) => {
    const data = booked(quote);
    return data !== undefined && 'error' in data ? data.error : null;
  };
  const undoRefused =
    unaccept.data?.code != null && unaccept.data !== settledUndo
      ? unacceptErrorKey(unaccept.data.code, job.status)
      : null;

  return (
    <article data-testid="job">
      <p>
        <Link to={`/${locale}/jobs`}>{t('job.back')}</Link>
      </p>
      <h1>{job.title ?? t('jobs.untitled')}</h1>
      <p className="mp-meta" data-testid="job-status">
        <Badge tone={jobTone(job.status)}>{t(`jobs.status.${job.status}`)}</Badge>
      </p>

      {auction === null ? null : (
        <AuctionHeader
          locale={locale}
          status={auction.status}
          closesAt={auction.closesAt}
          maxPriceCents={auction.maxPriceCents}
          durationDays={durationDaysOf(auction.createdAt, auction.closesAt)}
        />
      )}
      <JobFacts job={job} locale={locale} />

      {/* `W4-T09`: the start of work — the short-code form while AWARDED, the date once started. */}
      <StartPanel
        job={job}
        professional={
          quotes.find((quote) => quote.status === 'AWARDED')?.provider.displayName ?? null
        }
        locale={locale}
      />

      {/* `W5-T12` §4.10: back from Checkout — confirming, slow, or not charged. */}
      <AskReturnNotice returned={askReturn} jobId={job.id} draft={job.status === 'DRAFT'} />

      {job.status !== 'DRAFT' || askReturn === 'paid' ? null : askPrices === 'unavailable' ? (
        <>
          <p className="mp-lead">{t('job.draft.body')}</p>
          <AskUnavailable />
        </>
      ) : charging(askPrices) ? (
        // `W5-T12` §4.3: with charging on, publishing is paying; the free button would be refused.
        <>
          <p className="mp-lead">{t('job.draft.body')}</p>
          <AskDraftPublish prices={askPrices} locale={locale} busy={busy} error={publishError} />
        </>
      ) : job.status === 'DRAFT' ? (
        // `W4-T10` §2.4. Only on a draft: publishing is the one transition a client makes here.
        <Form method="post">
          <input type="hidden" name="intent" value="publish" />
          <p className="mp-lead">{t('job.draft.body')}</p>
          <Button type="submit" variant="primary" isDisabled={busy}>
            {t('job.publish')}
          </Button>
          {publishError === null ? null : <Alert tone="error">{t(publishError)}</Alert>}
        </Form>
      ) : null}

      {/* `W5-T12` §4.10: the windows paid for, or the auction's fee. */}
      <AskPaidFor
        job={job}
        ask={jobAsk}
        prices={charging(askPrices) ? askPrices : null}
        locale={locale}
        busy={busy}
        error={
          job.status !== 'DRAFT' && actionData?.error.startsWith('ask.') === true
            ? actionData.error
            : null
        }
      />

      {job.status === 'OPEN' && job.location === null ? (
        // `W4-T10` §2.3. The feed excludes a job with no address (`W4-T07` AC5). Since `W4-T11` the
        // form requires one, so this is a job posted before that — and an open job cannot be edited,
        // so posting again is the way out. Silence here would read as "nobody is interested".
        <p className="mp-lead" data-testid="job-no-address">
          {t('job.noAddress')}
        </p>
      ) : null}

      {auction !== null ? (
        <AuctionPanel
          auction={auction}
          bids={bids}
          now={now}
          locale={locale}
          busy={busy}
          error={actionData === undefined || actionData === null ? null : actionData.error}
        />
      ) : (
        <section className="mp-section" aria-labelledby="job-quotes">
          <h2 id="job-quotes">{t('job.quotes.title')}</h2>

          {quotes.length === 0 ? (
            <EmptyState
              title={t('job.quotes.empty.title')}
              description={t('job.quotes.empty.body')}
            />
          ) : (
            <>
              <p className="mp-meta">{t('job.quotes.count', { count: quotes.length })}</p>

              {/* A real label and a real control: re-sorting is the client's, per the slice rule. */}
              <label htmlFor="quote-sort">{t('job.quotes.sort.label')}</label>
              <select
                id="quote-sort"
                value={sort}
                onChange={(event) => setSort(event.target.value as QuoteSort)}
              >
                {QUOTE_SORTS.map((option) => (
                  <option key={option} value={option}>
                    {t(`job.quotes.sort.${option}`)}
                  </option>
                ))}
              </select>

              {undoRefused === null ? null : <Alert tone="error">{t(undoRefused)}</Alert>}

              <ul className="mp-list" data-testid="quote-list">
                {ranked.map((quote) => (
                  <li key={quote.id} className="mp-section" data-testid={`quote-${quote.id}`}>
                    <h3>
                      <Link to={`/${locale}/pro/${quote.provider.id}`}>
                        {quote.provider.displayName}
                      </Link>
                    </h3>
                    <p className="mp-lead">{money(quote.amountCents)}</p>
                    {/* `W4-T16`: the figure is before IVA; the client also reads what it comes to. */}
                    <QuotePrice amountCents={quote.amountCents} locale={locale} />
                    {/* The cheapest is marked whatever the sort — it is never allowed to be buried. */}
                    {quote.id === cheapest ? (
                      <p data-testid="cheapest">{t('job.quotes.cheapest')}</p>
                    ) : null}
                    <ProviderRating provider={quote.provider} locale={locale} />
                    {quote.breakdown === null ? null : <p>{quote.breakdown}</p>}
                    <Coverage quote={quote} locale={locale} />

                    {awardable(quote) ? (
                      <>
                        <AcceptedQuote
                          quote={quote}
                          busy={busy}
                          error={bookErrorFor(quote)}
                          onAward={() => {
                            setSettledUndo(unaccept.data ?? null);
                            void book.submit(
                              { intent: 'book', quoteId: quote.id },
                              { method: 'post' },
                            );
                          }}
                          onUndo={() => {
                            void unaccept.submit(
                              { intent: 'unaccept', quoteId: quote.id },
                              { method: 'post' },
                            );
                          }}
                        />
                        {checkoutFor(quote)}
                      </>
                    ) : quote.status === 'PENDING' ? (
                      <p>
                        {/* Accepting asks first; the undo is on the accepted quote (`W4-T17`). */}
                        <Button onPress={() => setConfirming(quote)} isDisabled={busy}>
                          {t('job.quotes.accept')}
                        </Button>{' '}
                        <Button
                          variant="secondary"
                          onPress={() => decide(quote, 'reject')}
                          isDisabled={busy}
                        >
                          {t('job.quotes.reject')}
                        </Button>
                      </p>
                    ) : (
                      // No control at all, rather than a disabled one that fails at the API: an
                      // expired or answered quote is not something the client can act on.
                      <p data-testid={`quote-state-${quote.id}`}>
                        <Badge tone={quoteTone(quote.status)}>
                          {t(`job.quotes.status.${quote.status}`)}
                        </Badge>
                      </p>
                    )}
                  </li>
                ))}
              </ul>

              {truncated ? <p className="mp-meta">{t('job.quotes.more')}</p> : null}
            </>
          )}
        </section>
      )}

      {threads === null || job.status === 'DRAFT' ? null : (
        <Messages locale={locale} threads={threads} selected={selected} />
      )}

      {confirming === null ? null : (
        <Dialog
          title={t('job.quotes.confirm.title')}
          isOpen
          onOpenChange={(open) => {
            if (!open) setConfirming(null);
          }}
          footer={
            <>
              <Button variant="secondary" onPress={() => setConfirming(null)}>
                {t('job.quotes.confirm.cancel')}
              </Button>{' '}
              <Button onPress={() => decide(confirming, 'accept')}>
                {t('job.quotes.confirm.accept')}
              </Button>
            </>
          }
        >
          {t('job.quotes.confirm.body', {
            provider: confirming.provider.displayName,
            amount: money(confirming.amountCents),
          })}
          <QuotePrice amountCents={confirming.amountCents} locale={locale} />
        </Dialog>
      )}
    </article>
  );
}

/** The first line of a message, short enough for a list row. */
function preview(body: string): string {
  const line = body.split('\n', 1)[0] ?? '';
  return line.length > 120 ? `${line.slice(0, 119)}…` : line;
}

/**
 * `W4-T06` — the conversations on this job, one per provider who wrote, and the one `?thread=` opens.
 *
 * The client never starts one: a provider asks first (spec §3.2), so the empty state says who can.
 */
function Messages({
  locale,
  threads,
  selected,
}: {
  locale: string;
  threads: JobThreadList;
  selected: JobThreadView | null;
}): ReactElement {
  const { t } = useTranslation();
  const open = selected?.thread ?? null;

  return (
    <section className="mp-section" aria-labelledby="job-threads">
      <h2 id="job-threads">{t('thread.client.title')}</h2>

      {threads.items.length === 0 ? (
        <p className="mp-meta" data-testid="threads-empty">
          {t('thread.client.empty')}
        </p>
      ) : (
        <ul className="mp-list" data-testid="thread-list">
          {threads.items.map((item) => (
            <li key={item.id} data-testid={`thread-${item.id}`}>
              <Link
                to={`?thread=${item.id}`}
                preventScrollReset
                aria-current={open?.id === item.id ? 'true' : undefined}
              >
                {item.provider.displayName}
              </Link>{' '}
              <span className="mp-meta">
                {t('thread.client.count', { count: item.messageCount })}
              </span>
              <p className="mp-meta">{preview(item.lastMessage.body)}</p>
            </li>
          ))}
        </ul>
      )}

      {selected === null || open === null ? null : (
        <section className="mp-section" aria-labelledby="job-thread-open">
          <h3 id="job-thread-open">
            {t('thread.client.with', { provider: open.provider.displayName })}
          </h3>
          <Thread
            view={selected}
            locale={locale}
            viewer="CLIENT"
            otherName={open.provider.displayName}
            threadId={open.id}
            emptyText={t('thread.client.empty')}
          />
        </section>
      )}
    </section>
  );
}

/**
 * `W6-T06` — the client's side of an auction: the bids, live, cheapest first, and a choice.
 *
 * Amended after the operator's review, 2026-10-01: the client sees each bid as it arrives (the poll
 * brings it) and may choose at any time; choosing while bidding runs ends the auction, and the
 * confirmation says so. Providers still see only their own. The award asks first: there is no undo.
 */
function AuctionPanel({
  auction,
  bids,
  now,
  locale,
  busy,
  error,
}: {
  auction: Auction;
  bids: AuctionBid[];
  now: string;
  locale: string;
  busy: boolean;
  error: string | null;
}): ReactElement {
  const { t } = useTranslation();
  const submit = useSubmit();
  const [confirming, setConfirming] = useState<AuctionBid | null>(null);

  const money = (cents: number): string =>
    (cents / 100).toLocaleString(locale, { style: 'currency', currency: 'EUR' });
  const date = (iso: string): string =>
    new Date(iso).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });

  const award = (bid: AuctionBid): void => {
    setConfirming(null);
    void submit({ intent: 'award', bidId: bid.id }, { method: 'post' });
  };

  const chosen = bids.find((bid) => bid.id === auction.awardedBidId) ?? null;

  return (
    <section className="mp-section" aria-labelledby="job-auction" data-testid="auction-panel">
      <h2 id="job-auction">{t('auction.bids.title')}</h2>

      {auction.status === 'OPEN' ? (
        <>
          <Countdown closesAt={auction.closesAt} now={now} />
          <p className="mp-meta" data-testid="auction-privacy">
            {t('auction.privacy')}
          </p>
          {bids.length === 0 ? (
            <p className="mp-lead" data-testid="auction-no-bids-yet">
              {t('auction.noBidsYet')}
            </p>
          ) : null}
        </>
      ) : null}

      {auction.status === 'CLOSED' && auction.closedAt !== null ? (
        <p className="mp-lead" data-testid="auction-choose-by">
          {t('auction.chooseBy', {
            date: date(
              new Date(
                new Date(auction.closedAt).getTime() + AWARD_WINDOW_DAYS * 86_400_000,
              ).toISOString(),
            ),
          })}
        </p>
      ) : null}

      {auction.status === 'AWARDED' && chosen !== null ? (
        <p className="mp-lead" data-testid="auction-awarded">
          {t('auction.awarded', {
            provider: chosen.provider.displayName,
            amount: money(chosen.amountCents),
          })}
        </p>
      ) : null}

      {auction.status === 'EXPIRED' ? (
        <p className="mp-lead" data-testid="auction-expired">
          {t(bids.length === 0 ? 'auction.expired.noBids' : 'auction.expired.notChosen')}
        </p>
      ) : null}

      {auction.status === 'CANCELLED' ? (
        <p className="mp-lead" data-testid="auction-cancelled">
          {t('auction.cancelled')}
        </p>
      ) : null}

      {bids.length === 0 ? null : (
        <ul className="mp-list" data-testid="auction-bids">
          {bids.map((bid, index) => (
            <li key={bid.id} className="mp-section" data-testid={`auction-bid-${bid.id}`}>
              <h3>
                <Link to={`/${locale}/pro/${bid.provider.id}`}>{bid.provider.displayName}</Link>
              </h3>
              <p className="mp-lead">{money(bid.amountCents)}</p>
              {/* Cheapest first, and marked: the list is sorted by the API, the mark is the screen's. */}
              {index === 0 ? <p data-testid="auction-cheapest">{t('auction.cheapest')}</p> : null}
              <ProviderRating provider={bid.provider} locale={locale} />
              {bid.message === null ? null : <p>{bid.message}</p>}
              {/* Choosing is open while bidding runs and after it (operator, 2026-10-01). */}
              {auction.status === 'OPEN' || auction.status === 'CLOSED' ? (
                <Button onPress={() => setConfirming(bid)} isDisabled={busy}>
                  {t('auction.choose')}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {error === null ? null : <Alert tone="error">{t(error)}</Alert>}

      {confirming === null ? null : (
        <Dialog
          title={t('auction.confirm.title')}
          isOpen
          onOpenChange={(open) => {
            if (!open) setConfirming(null);
          }}
          footer={
            <>
              <Button variant="secondary" onPress={() => setConfirming(null)}>
                {t('auction.confirm.cancel')}
              </Button>{' '}
              <Button onPress={() => award(confirming)}>{t('auction.confirm.accept')}</Button>
            </>
          }
        >
          {t(auction.status === 'OPEN' ? 'auction.confirm.bodyOpen' : 'auction.confirm.body', {
            provider: confirming.provider.displayName,
            amount: money(confirming.amountCents),
          })}
        </Dialog>
      )}
    </section>
  );
}
